import React, { useEffect, useMemo, useState } from "react";
import "./CompanyDetails.css";
import myImage from "../../../assets/images/contact.svg.png";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { routeNames } from "../../../consts/routesNames";
import trash from "../../../assets/images/trash.svg";
import Button from "../../../components/common/button/Button";
import appConfig from "../../../config.json";
import { axios } from "../../../utils/axiosConfig";
import { BACKEND_URL } from "../../../constants";
import { PERMISSIONS } from "../../../auth/utils/permissionCodes";
import { useCan } from "../../../auth/hooks/usePermissions";
import { useUpdateOwnership } from "../../../auth/hooks/useUpdateOwnership";
import { getCompanyDetails } from "../../../apis/companiesApis/getCompanyDetails";
import { openModal } from "../../../redux/modal/modalActions";
import ResponseModal from "../../../components/common/modal/responseModal/ResponseModal";
import EntityLinksSection from "../../../components/entityLinks/EntityLinksSection";

function unwrapDetailPayload(raw) {
  if (raw == null) return null;
  if (typeof raw === "object" && raw.data !== undefined && raw.data !== null) {
    return raw.data;
  }
  return raw;
}

function CompanyDetails() {
  const backEndUrl = `${BACKEND_URL}`;
  const canUpdateCompanyImagePermission = useCan(PERMISSIONS.COMPANIES.IMAGE_UPDATE);
  const { canUpdateOwnedRecord } = useUpdateOwnership();

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const stateRow = location.state?.data;

  const resolvedCompanyId = useMemo(() => {
    const raw = searchParams.get("id");
    const parsed = raw != null && raw !== "" ? parseInt(raw, 10) : NaN;
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    return stateRow?.id;
  }, [searchParams, stateRow]);

  const [companyData, setCompanyData] = useState(() => stateRow ?? null);
  const [relatedProperties, setRelatedProperties] = useState([]);
  const [relatedCompanies, setRelatedCompanies] = useState([]);
  const [relatedContacts, setRelatedContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemImage, setItemImage] = useState(stateRow?.profile_picture ?? null);

  const canUpdateCompanyImage = canUpdateOwnedRecord(
    companyData?.created_by,
    canUpdateCompanyImagePermission,
  );

  useEffect(() => {
    if (!resolvedCompanyId) {
      dispatch(openModal(<ResponseModal text="No company selected. Open a company from the list." isError={true} />));
      navigate(routeNames.COMPANIES, { replace: true });
      return;
    }

    let cancelled = false;

    const loadCompanyDetail = async () => {
      setLoading(true);
      const detailsRes = await getCompanyDetails(resolvedCompanyId);
      if (cancelled) return;

      if (detailsRes?.error_code === 2 || detailsRes?.error_code === 3) {
        dispatch(openModal(<ResponseModal text={detailsRes.error_message ?? "Could not load company details."} isError={true} />));
        navigate(routeNames.COMPANIES, { replace: true });
        return;
      }

      const detail = unwrapDetailPayload(detailsRes.res?.data);

      if (cancelled || detail == null || detail.id == null) {
        if (!cancelled) {
          dispatch(openModal(<ResponseModal text="Could not load company details." isError={true} />));
          navigate(routeNames.COMPANIES, { replace: true });
        }
        return;
      }

      if (Number(detail.id) !== Number(resolvedCompanyId)) return;

      setCompanyData(detail);
      setItemImage(detail.profile_picture ?? null);
      setRelatedProperties(detail.related_properties ?? []);
      setRelatedCompanies(detail.related_companies ?? []);
      setRelatedContacts(detail.related_contacts ?? []);
      setLoading(false);
    };

    loadCompanyDetail();

    return () => {
      cancelled = true;
    };
  }, [dispatch, navigate, resolvedCompanyId]);

  useEffect(() => {
    if (resolvedCompanyId && searchParams.get("id") !== String(resolvedCompanyId)) {
      navigate(`${routeNames.COMPANY_DETAILS}?id=${resolvedCompanyId}`, {
        replace: true,
        state: location.state,
      });
    }
  }, [resolvedCompanyId, navigate, searchParams, location.state]);

  const handleImageChange = (e) => {
    if (e.target.files[0]) changeImages(e.target.files[0]);
  };

  const deleteImage = () => {
    if (!canUpdateCompanyImage) return;
    changeImages();
  };

  const changeImages = (image) => {
    if (!canUpdateCompanyImage) return;
    const url = backEndUrl + appConfig.backend.routes.companies.updateCompanyImages;

    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
    };

    axios
      .post(
        url,
        {
          company_id: resolvedCompanyId,
          profile_picture: image ? image : {},
        },
        config,
      )
      .then(() => {
        setItemImage(image ? URL.createObjectURL(image) : null);
      });
  };

  if (!resolvedCompanyId) {
    return null;
  }

  if (!companyData) {
    return (
      <div className="main-container-companyDetails">
        <div className="section-main-container-companyDetails">
          <p className="company-details-loading">Loading company…</p>
        </div>
      </div>
    );
  }

  const data = companyData;

  return (
    <>
      <div className="main-container-companyDetails">
        <div className="section-main-container-companyDetails">
          <div className="section-row-container-companyDetails">
            <div className="section-container-companyDetails">
              <div className="section-container-companyDetails-companyInfo">
                <div className="column-companyInfo-profile-picture">
                  <div>
                    <img src={itemImage ? itemImage : myImage} alt="Company" />
                  </div>
                  {canUpdateCompanyImage && (
                    <div className="image-controls">
                      <label className="image-btn">
                        <input
                          type="file"
                          id="propertyImage"
                          accept="image/*,.pdf"
                          onChange={(e) => handleImageChange(e)}
                        />
                        Edit Image
                      </label>
                      {itemImage !== null ? (
                        <Button
                          className="delete-image-btn-items"
                          buttonText={<img style={{ height: "75%" }} src={trash} alt="Delete" />}
                          onClick={() => deleteImage()}
                        />
                      ) : null}
                    </div>
                  )}
                </div>
                <div className="column-companyInfo">
                  <h2> {data.name} Details</h2>
                  <div className="label-text-container">
                    <p>
                      <strong>Street Number:</strong> {data.street_number}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Street Name:</strong> {data.street_name}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>City:</strong> {data.city}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>State:</strong> {data.state}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Country:</strong> {data.country}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Zip Code:</strong> {data.zip_code}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Category:</strong> {data.category_name ?? data.category?.name ?? ""}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Website:</strong> {data.website !== null ? data.website : ""}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Email:</strong> {data.email != null && String(data.email).trim() !== "" ? data.email : ""}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Comment:</strong> {data.comment}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Bank info — hidden for now
          <div className="section-row-container-companyDetails">
            <div className="section-container-companyDetails">
              <div className="section-container-companyDetails-bankInfo">
                <div className="column-companyInfo">
                  <h2>{data.name} Bank Info</h2>
                  <div className="label-text-container">
                    <p>
                      <strong>Bank Name:</strong>{" "}
                      {data.company_bank && data.company_bank.name !== null ? data.company_bank.name : ""}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Account Name:</strong> {data.company_bank_account_name}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Account Type:</strong> {data.company_bank_account_type}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Account Number:</strong> {data.company_bank_account_number}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Routing Number:</strong> {data.company_bank_routing_number}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Street Number:</strong> {data.bank_street_number}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Street Name:</strong> {data.bank_street_name}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>City:</strong> {data.bank_city}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>State:</strong> {data.bank_state}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Country:</strong> {data.bank_country}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      <strong>Zip Code:</strong> {data.bank_zip_code}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          */}
          <div className="section-row-container-companyDetails">
            <div className="section-container-companyDetails">
              <div className="section-container-companyDetails-links">
                <EntityLinksSection
                  relatedProperties={relatedProperties}
                  relatedCompanies={relatedCompanies}
                  relatedContacts={relatedContacts}
                  loading={loading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CompanyDetails;
